/* eslint-disable prefer-const */

import { PayloadAction } from "@reduxjs/toolkit";
import { closeAppBackdrop, notify, showAppBackdrop } from "features/app/store/slice";
import { call, put, takeEvery } from "redux-saga/effects";
import { IApiResponse } from "types/api";
import { IGroupPermissionData, IOptionsPermissionData } from "types/models/permission-management";
import { formatPath } from "utils";
import { apiDelete, apiGet, apiPost } from "utils/api";
import PermissionManagementPaths from "../models/paths";
import { createNewGroupPermission, createNewGroupPermissionSuccess, deleteGroupPermission, deleteGroupPermissionSuccess, editGroupPermission, editGroupPermissionSuccess, fetchGroupPermissions, fetchGroupPermissionsSuccess, fetchOptionPermissions, fetchOptionPermissionsSuccess } from "./slice";
import { handleFetchUsers } from "features/user-management/store/saga";
import { fetchDataUser } from "features/user-management/store/slice";

const apiFetchGroupPermissions = async () => {
  return apiGet<any>(formatPath(PermissionManagementPaths.get_group_permission));
};

function* handleFetchGroupPermissions() {
  try {
    const response: IApiResponse<IGroupPermissionData[]> = yield call(apiFetchGroupPermissions);
    if (response.data) {
      yield put(fetchGroupPermissionsSuccess(response.data));
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

const apiFetchOptionPermissions = async () => {
  return apiGet<any>(formatPath(PermissionManagementPaths.get_option_permission));
};

function* handleFetchOptionPermissions() {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<IOptionsPermissionData[]> = yield call(apiFetchOptionPermissions);
    if (response.data) {
      yield put(fetchOptionPermissionsSuccess(response.data));
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

const apiCreateNewGroupPermissions = async (data: any) => {
  return apiPost<any>(formatPath(PermissionManagementPaths.add_group_permission), data);
};

function* handleCreateNewGroupPermission(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiCreateNewGroupPermissions, action.payload);
    if (response.data) {
      yield put(createNewGroupPermissionSuccess(response.data.data));
      yield put(
        notify(`${response.data.detail}`, {
          options: { variant: 'success' },
        })
      );
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiEditGroupPermission = async (data: any) => {
  const response = await apiPost<any>(formatPath(PermissionManagementPaths.update_group_permission), data);
  return response;
};

function* handleEditGroupPermission(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiEditGroupPermission, action.payload);
    if (response.data) {
      yield put(editGroupPermissionSuccess(response.data.data));
      yield put(
        notify(`${response.data.detail}`, {
          options: { variant: 'success' },
        })
      );
      yield call(handleFetchUsers);
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

const apiDeleteGroupPermission = async (data: any) => {
  return apiDelete<any>(formatPath(PermissionManagementPaths.delete_group_permission), data);
};

function* handleDeleteGroupPermission(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiDeleteGroupPermission, action.payload);
    if (response.data) {
      yield put(deleteGroupPermissionSuccess(response.data.data));
      yield put(
        notify(`${response.data.detail}`, {
          options: { variant: 'success' },
        })
      );
      yield put(fetchDataUser())
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
    }
    yield put(closeAppBackdrop());
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export default function* PermissionManagementSaga() {
  yield takeEvery(fetchGroupPermissions.type, handleFetchGroupPermissions);
  yield takeEvery(fetchOptionPermissions.type, handleFetchOptionPermissions);
  yield takeEvery(createNewGroupPermission.type, handleCreateNewGroupPermission);
  yield takeEvery(editGroupPermission.type, handleEditGroupPermission);
  yield takeEvery(deleteGroupPermission.type, handleDeleteGroupPermission);
}
